import type { LatLon } from "@radar-sim/schema";
import type { AreaData, FerryTemplate, Mark } from "./types.js";
import { WEYMOUTH_PORTLAND_COASTLINE } from "./weymouth-portland-coastline.js";

/**
 * Weymouth / Portland — secondary teaching area. Coastline traces the
 * eastern face of Portland from Portland Bill round to Portland Harbour,
 * the northern shore of Portland Harbour, the curve of Weymouth Bay, and
 * the start of the Chesil Beach exposure. Coordinates approximate, hand-
 * curated for teaching scenarios.
 *
 * Portland Race — a vicious tidal-anomaly extending south of Portland Bill
 * — is modelled as a charted polygon with an associated `tidalAnomalyId`
 * available to scenarios. The engine does not move vessels through it
 * directly in v1, but the rendered overlay and the scenario authoring UI
 * surface it.
 */

const COASTLINE = WEYMOUTH_PORTLAND_COASTLINE;

const DEPTH_CONTOURS = [
  // Approximate 20 m line around the south-east of Portland Bill —
  // marks the inshore edge of the deep-water route.
  {
    metres: 20,
    line: [
      { lat: 50.49, lon: -2.46 },
      { lat: 50.49, lon: -2.43 },
      { lat: 50.5, lon: -2.4 },
      { lat: 50.515, lon: -2.39 },
    ],
  },
];

const MARKS: ReadonlyArray<Mark> = [
  {
    id: "portland-bill-light",
    name: "Portland Bill lighthouse",
    position: { lat: 50.516, lon: -2.456 },
    type: "light",
    rcs: 0.7,
  },
  {
    id: "shambles-east",
    name: "Shambles E cardinal",
    position: { lat: 50.518, lon: -2.41 },
    type: "buoy",
    rcs: 0.1,
  },
  {
    id: "shambles-west",
    name: "Shambles W cardinal",
    // Real Shambles W cardinal sits in the gap between Portland Bill
    // (lat ~50.51) and the Shambles bank — push slightly east to clear
    // the precise OSM Portland polygon's eastern edge.
    position: { lat: 50.524, lon: -2.46 },
    type: "buoy",
    rcs: 0.1,
  },
  {
    id: "portland-harbour-n",
    name: "Portland Harbour N entrance",
    position: { lat: 50.595, lon: -2.444 },
    type: "post",
    rcs: 0.15,
  },
  {
    id: "portland-harbour-s",
    name: "Portland Harbour S entrance",
    position: { lat: 50.583, lon: -2.45 },
    type: "post",
    rcs: 0.15,
  },
  {
    id: "weymouth-pierhead",
    name: "Weymouth pierhead light",
    position: { lat: 50.609, lon: -2.444 },
    type: "light",
    rcs: 0.4,
  },
  {
    id: "lulworth-firing-marker",
    name: "Lulworth firing range marker",
    position: { lat: 50.6, lon: -2.25 },
    type: "buoy",
    rcs: 0.05,
  },
];

/**
 * Portland Race — schematised polygon south of the Bill where tidal streams
 * over an underwater ledge produce overfalls. Surfaced to the renderer as
 * a charted hazard zone; scenarios may reference it via tidalAnomalyId.
 */
export interface TidalAnomaly {
  id: string;
  name: string;
  /** Polygon ring describing the disturbed-water area. */
  polygon: ReadonlyArray<LatLon>;
  /** Severity 0..1 — used by the renderer to vary the overlay intensity. */
  severity: number;
  /** Optional set/drift inside the polygon: the local tidal stream
   *  pushes a vessel passing through the zone in this direction at
   *  this rate. Engine multiplies this by severity to get the
   *  applied effect. Mid-tide / spring estimate; flow may vary with
   *  the actual tide phase but we don't model that yet. */
  drift?: { bearingDeg: number; speedKnots: number };
}

const TIDAL_ANOMALIES: ReadonlyArray<TidalAnomaly> = [
  {
    id: "portland-race",
    name: "Portland Race",
    severity: 0.85,
    polygon: [
      { lat: 50.49, lon: -2.5 },
      { lat: 50.504, lon: -2.45 },
      { lat: 50.516, lon: -2.42 },
      { lat: 50.51, lon: -2.39 },
      { lat: 50.49, lon: -2.4 },
      { lat: 50.475, lon: -2.45 },
    ],
  },
];

const LANES: ReadonlyMap<string, ReadonlyArray<LatLon>> = new Map([
  // Portland deep-water route — east-side passage well clear of the Race.
  [
    "portland-east-passage-southbound",
    [
      { lat: 50.6, lon: -2.39 },
      { lat: 50.57, lon: -2.39 },
      { lat: 50.54, lon: -2.4 },
      { lat: 50.5, lon: -2.4 },
      { lat: 50.46, lon: -2.41 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Channel into Weymouth.
  [
    "weymouth-approach",
    [
      { lat: 50.59, lon: -2.4 },
      { lat: 50.6, lon: -2.42 },
      { lat: 50.609, lon: -2.444 },
    ] as ReadonlyArray<LatLon>,
  ],
]);

const FERRY_TEMPLATES: ReadonlyArray<FerryTemplate> = [
  {
    id: "condor-poole-channel-islands",
    name: "Condor (Weymouth–Channel Islands) — past the Bill",
    vesselType: "ferry",
    rcs: 0.95,
    ais: {
      mmsi: "232051001",
      name: "CONDOR LIBERATION",
      class: "A",
      destination: "ST HELIER",
      callsign: "MFCL2",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 50.61, lon: -2.45 }, cog: 180, sog: 14 },
      { atSimSeconds: 600, position: { lat: 50.55, lon: -2.4 }, cog: 175, sog: 22 },
      { atSimSeconds: 1500, position: { lat: 50.46, lon: -2.4 }, cog: 175, sog: 24 },
    ],
  },
];

export const WEYMOUTH_PORTLAND: AreaData = {
  area: "weymouth-portland",
  boundingBox: {
    nw: { lat: 50.65, lon: -2.55 },
    se: { lat: 50.42, lon: -2.2 },
  },
  coastline: COASTLINE,
  depthContours: DEPTH_CONTOURS,
  marks: MARKS,
  lanes: LANES,
  raconIds: new Set(),
  ferryTemplates: FERRY_TEMPLATES,
};

export { TIDAL_ANOMALIES };
